/**
 * 62.54 step 3 (ARCHITECTURE §5.6 "62.54, the design"): the planet's wind field — one pure function of (seed, planet,
 * place, second), the wind only (the cells carry weather from step 5). Two layers sum:
 *
 * - **Bands, the backbone.** A prevailing wind along circles of latitude about the planet's axis (planets do not spin, so
 *   the axis is a seeded tilt off the system's up, +y): easterly from the equator to 30°, westerly from 30° to 60°,
 *   easterly again to the pole — `−U · sin(6|φ|)` along the east, zero at the band edges where the cells form.
 *   `U` is WIND_FIELD.bands, a share of the planet's surface gravity.
 * - **Cells: the weather systems.** Each segment of the planet's keyed weather timeline (core/weather.ts weatherAt) is
 *   a cell of that segment's kind — a low (rain, storm, dust, ash, fog) or a high (clear, haze) — born a segment before
 *   its own and gone WIND_FIELD.cellLife segments later. A segment has WIND_FIELD.cellsPerSegment cells (the first's
 *   kind from the planet's timeline, the next's from a second keyed timeline with the same mix), each on its own keyed
 *   latitude within ±WIND_FIELD.cellLatMax and keyed longitude (62.54 step 5: so fronts come and go anywhere on the
 *   planet, not along one track), riding its band at WIND_FIELD.cellDrift radians a segment, the way its band blows. A low turns its
 *   wind about its centre (one way in each hemisphere) with a little inflow; a high turns the other way, gently. The
 *   wind round a centre is a vortex with a calm eye: `∝ (r/R)·e^(−r²/R²)`, peaking at the kind's wind share.
 *
 * Positions are closed-form in time — nothing is integrated, so any second is asked directly and the same on every host.
 * The noise layers (curl noise) have a slot in the note, not here. The result is a world-space vector along the ground
 * (units: acceleration, a share of G·M/r², as decision 120's planetWind), or null without the wind switch. Pure: dmath.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { WEATHER_EFFECTS, weatherAt, WEATHER_SEGMENT_S, type WeatherKind } from './weather';
import { WIND_FIELD } from '../constants/action';
import { G } from '../constants/physics';
import { SeededRandom, hashKey } from './random';
import { dacos, dasin, dcos, dexp, dsin } from './dmath';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';

type FieldState = Pick<GameState, 'config' | 'turnNumber' | 'planets'>;

/** A planet's frame: its axis, two directions across it (longitude 0 and 90°), and the reference place the timeline describes. */
interface PlanetFrame { axis: Vec3; e1: Vec3; e2: Vec3 }
const frames = new Map<string, PlanetFrame>();
function frameOf(seed: string, planet: Pick<PlanetState, 'id'>): PlanetFrame {
  const key = `${seed}-${planet.id}`;
  let f = frames.get(key);
  if (f) return f;
  const r = new SeededRandom(`${key}-windaxis`);
  const tilt = r.range(0, WIND_FIELD.axisTilt), turn = r.range(0, 2 * Math.PI);
  const axis = vecNormalize({ x: dsin(tilt) * dcos(turn), y: dcos(tilt), z: dsin(tilt) * dsin(turn) });
  const e1 = vecNormalize(vecCross(axis, Math.abs(axis.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 }));
  f = { axis, e1, e2: vecCross(axis, e1) };
  frames.set(key, f);
  return f;
}

/** The planet's wind axis in a match of seed `seed` (GameConfig.seed): the bands blow east about it (the forest's lee reads it too). */
export function windAxis(seed: string, planet: Pick<PlanetState, 'id'>): Vec3 {
  return frameOf(seed, planet).axis;
}

/** The place at latitude `lat`, longitude `lon` in a frame (unit). */
function placeAt(fr: PlanetFrame, lat: number, lon: number): Vec3 {
  const c = dcos(lat);
  return vecAdd(vecAdd(vecScale(fr.e1, c * dcos(lon)), vecScale(fr.e2, c * dsin(lon))), vecScale(fr.axis, dsin(lat)));
}

/** The bands' wind along the east at latitude `lat` (radians), as a share of surface gravity: −U·sin(6|φ|). */
export function bandShare(lat: number, u: number = WIND_FIELD.bands): number {
  return -u * dsin(6 * Math.abs(lat));
}

/** The weather's time in segments: the action clock's seconds over a segment; the turn clock's round, at its middle. */
export function weatherTime(state: Pick<GameState, 'config' | 'turnNumber'>, seconds: number): number {
  return state.config.clock === 'action' ? seconds / (state.config.weatherSegmentS ?? WEATHER_SEGMENT_S) : state.turnNumber + 0.5;   // 67.42: the Lab's segment
}

const LOWS: ReadonlySet<WeatherKind> = new Set(['rain', 'storm', 'dust', 'ash', 'fog']);

/** One cell now: its centre (unit), its radius (radians), its signed spin share (+ turns like a northern low), and its inflow share. */
export interface WindCell { /** 67.48: stable for the cell's life (its birth segment × 64 + its index): what keys its drawn shape. Presentation reads it; the sim does not. */ id?: number; kind: WeatherKind; centre: Vec3; radius: number; spin: number; inflow: number; /** 0–1: how far through its birth or its fading (1 in its prime). */ ease: number; /** cos of 4 radii: a place whose dot with the centre is below it feels none of the cell's wind (the cheap test before the arc). */ cosReach: number }

/**
 * 62.54 step 5: where segment k's cell j rides — a keyed latitude within ±WIND_FIELD.cellLatMax and a keyed longitude at
 * its segment's middle — drawn once and kept (every field and weather query asks).
 */
const draws = new Map<string, { lat: number; lon: number }>();
function cellDraw(key: string, k: number, j: number): { lat: number; lon: number } {
  const id = `${key}|${k}|${j}`;
  let d = draws.get(id);
  if (!d) {
    const r = new SeededRandom(`${key}-windcell-${k}-${j}`);
    d = { lat: r.range(-WIND_FIELD.cellLatMax, WIND_FIELD.cellLatMax), lon: r.range(0, 2 * Math.PI) };
    draws.set(id, d);
  }
  return d;
}

/** The cells live on a planet at weather time `tau` (segments). Pure, closed-form. */
export function cellsAt(state: FieldState, planetIndex: number, tau: number): WindCell[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  // 62.57 cut 5: the last list for this config and planet object at this time, without building the keys below (a
  // sight line asks once a sample); the keyed memo is the same list for a new object
  const quick = cellsQuick.get(state.config)?.get(planet);
  if (quick && quick.tau === tau) return quick.cells;
  const seed = String(state.config.seed ?? '');
  const key = `${seed}-${planet.id}`;
  // the last list per planet and weather (a tick's queries, a grid's nodes, share a second): the same list, pure
  const memoKey = `${key}|${planet.archetype}|${state.config.weatherFixed ?? ''}|${state.config.weatherCellScale ?? 1}|${state.config.weatherCellsPerSegment ?? WIND_FIELD.cellsPerSegment}`;
  const last = lastCells.get(memoKey);
  const cells = last && last.tau === tau ? last.cells : buildCells(state, planet, key, tau);
  if (cells !== last?.cells) lastCells.set(memoKey, { tau, cells });
  let byPlanet = cellsQuick.get(state.config);
  if (!byPlanet) { byPlanet = new WeakMap(); cellsQuick.set(state.config, byPlanet); }
  byPlanet.set(planet, { tau, cells });
  return cells;
}
const lastCells = new Map<string, { tau: number; cells: WindCell[] }>();
/** 62.57 cut 5: the last list by the config and planet objects. */
const cellsQuick = new WeakMap<object, WeakMap<object, { tau: number; cells: WindCell[] }>>();

function buildCells(state: FieldState, planet: PlanetState, key: string, tau: number): WindCell[] {
  const fr = frameOf(String(state.config.seed ?? ''), planet);
  const out: WindCell[] = [];
  const k0 = Math.floor(tau);
  for (let k = Math.max(0, k0 - 2); k <= k0 + 1; k++) {
    // life: born a segment before its own, gone WIND_FIELD.cellLife segments later; eased in and out
    const age = tau - (k - 1), life = WIND_FIELD.cellLife;
    if (age <= 0 || age >= life) continue;
    const ease = Math.min(1, age / 0.5, (life - age) / 0.5);
    for (let j = 0; j < (state.config.weatherCellsPerSegment ?? WIND_FIELD.cellsPerSegment); j++) {   // 67.42: the Lab's count
    // the kinds: the planet's keyed timeline for the first cell, a second keyed timeline (the same mix and persistence) for the next
    const kind = state.config.weatherFixed ?? weatherAt(j === 0 ? key : `${key}-cell${j}`, planet.archetype, k);
    const share = WEATHER_EFFECTS[kind].wind;
    const low = LOWS.has(kind);
    // 62.54 step 5: each cell rides its own keyed latitude, from its own keyed longitude at its segment's middle
    // 67.42: the Lab's size scales the weather systems, not the highs (a clear or hazy cell is a cell too: grown, it would
    // crowd the wet ones out — the strongest cover wins at a place, core/weatherCells.ts)
    const radius = (WIND_FIELD.cellRadius[kind] ?? WIND_FIELD.cellRadius.rain) * (kind === 'clear' || kind === 'haze' ? 1 : (state.config.weatherCellScale ?? 1));
    const { lat, lon: lon0 } = cellDraw(key, k, j);
    const way = Math.sign(bandShare(lat)) || 1;   // it drifts the way its band blows
    const lon = lon0 + way * WIND_FIELD.cellDrift * (tau - (k + 0.5));
    const north = lat >= 0 ? 1 : -1;
    const strength = (low ? Math.max(share, WIND_FIELD.lowFloor) : Math.max(share, WIND_FIELD.highShare)) * ease;
    out.push({ id: k * 64 + j, kind, centre: placeAt(fr, lat, lon), radius, spin: (low ? 1 : -WIND_FIELD.highSpin) * north * strength, inflow: low ? WIND_FIELD.inflow * strength : -WIND_FIELD.inflow * 0.5 * strength, ease, cosReach: dcos(Math.min(Math.PI, 4 * radius)) });
    }
  }
  return out;
}

/** The vortex's shape at angle `r` from a centre of radius `R`: 0 at the eye, 1 at its peak (r = R/√2), gone a few radii out. */
function vortex(r: number, R: number): number {
  const x = r / R;
  return x * dexp(-x * x) * 2.3316;   // 2.3316 = √(2e): the peak of x·e^(−x²) scaled to 1
}

/**
 * The field at a place on a planet at a second: bands + cells, along the ground, as an acceleration (a share of the
 * planet's surface gravity). Null without the wind switch. Before the gust and the shelter (core/wind.ts adds them).
 */
export function windField(state: FieldState, planetIndex: number, dir: Vec3, seconds = 0): Vec3 | null {
  const p = fieldParts(state, planetIndex, seconds);
  return p ? fieldWith(p, dir) : null;
}

/**
 * 69.67 (a strike's lay asks the frozen field thousands of times a call): `windField` at one second with what no place
 * changes — the planet's surface gravity, its frame, its cells then, the seed — taken once; each place's the same arithmetic.
 */
export function windFieldAt(state: FieldState, planetIndex: number, seconds: number): ((dir: Vec3) => Vec3) | null {
  const p = fieldParts(state, planetIndex, seconds);
  return p ? (dir: Vec3) => fieldWith(p, dir) : null;
}

interface FieldParts { g: number; fr: PlanetFrame; cells: readonly WindCell[]; seed: string; planet: PlanetState; tau: number }
function fieldParts(state: FieldState, planetIndex: number, seconds: number): FieldParts | null {
  if (!state.config.wind) return null;
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const seed = String(state.config.seed ?? ''), tau = weatherTime(state, seconds);
  return { g: G * planet.mass / (planet.radius * planet.radius), fr: frameOf(seed, planet), cells: cellsAt(state, planetIndex, tau), seed, planet, tau };
}

function fieldWith(p: FieldParts, dir: Vec3): Vec3 {
  const { g, fr } = p;
  const n = vecNormalize(dir);
  // the bands, along the east about the axis
  let w: Vec3 = { x: 0, y: 0, z: 0 };
  const eastRaw = vecCross(fr.axis, n);
  const eastLen = vecLength(eastRaw);
  if (eastLen > 1e-9) {
    const lat = dasin(Math.max(-1, Math.min(1, vecDot(n, fr.axis))));
    w = vecScale(eastRaw, (bandShare(lat) * g) / eastLen);
  }
  // the cells: spin about each centre (c × n turns round it), inflow toward it
  for (const c of p.cells) {
    const cosR = Math.max(-1, Math.min(1, vecDot(c.centre, n)));
    if (cosR < c.cosReach) continue;   // beyond 4 radii: none of its wind (the arc below would say the same)
    const r = dacos(cosR);
    if (r < 1e-9 || r > 4 * c.radius) continue;
    const shape = vortex(r, c.radius);
    const round = vecCross(c.centre, n), roundLen = vecLength(round);
    if (roundLen < 1e-12) continue;
    w = vecAdd(w, vecScale(round, (c.spin * shape * g) / roundLen));
    const toward = vecSub(c.centre, vecScale(n, cosR)), towardLen = vecLength(toward);
    if (towardLen > 1e-12) w = vecAdd(w, vecScale(toward, (c.inflow * shape * g) / towardLen));
  }
  // 62.54 step 8: swirls and turbulence between the cells — a low-frequency curl noise on the sphere
  if (WIND_FIELD.noise.share > 0) w = vecAdd(w, vecScale(curlNoise(p.seed, p.planet, n, p.tau), WIND_FIELD.noise.share * g));
  return w;
}

// ---------------------------------------------------------------------------
// 62.54 step 8: the first noise layer — a curl noise on the sphere
// ---------------------------------------------------------------------------

/** A lattice value in [−1, 1) from a key and an integer corner (the noise's corners; integer hashing only). */
function corner(key: number, x: number, y: number, z: number): number {
  let h = Math.imul(key ^ Math.imul(x, 0x8da6b343), 0x85ebca6b);
  h = Math.imul(h ^ Math.imul(y, 0xd8163841), 0xc2b2ae35);
  h = Math.imul(h ^ Math.imul(z, 0xcb1ab31f), 0x27d4eb2f);
  h ^= h >>> 15;
  return ((h >>> 0) / 4294967296) * 2 - 1;
}

/**
 * Smooth value noise in three dimensions and its gradient at `p`: the eight lattice corners round it, blended by the
 * smoothstep s(t) = t²(3 − 2t) along each axis (whose slope 6t(1 − t) gives the gradient exactly). Pure arithmetic.
 */
export function valueNoiseGrad(key: number, px: number, py: number, pz: number): { v: number; gx: number; gy: number; gz: number } {
  const ix = Math.floor(px), iy = Math.floor(py), iz = Math.floor(pz);
  const fx = px - ix, fy = py - iy, fz = pz - iz;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  const dx = 6 * fx * (1 - fx), dy = 6 * fy * (1 - fy), dz = 6 * fz * (1 - fz);
  const c000 = corner(key, ix, iy, iz), c100 = corner(key, ix + 1, iy, iz), c010 = corner(key, ix, iy + 1, iz), c110 = corner(key, ix + 1, iy + 1, iz);
  const c001 = corner(key, ix, iy, iz + 1), c101 = corner(key, ix + 1, iy, iz + 1), c011 = corner(key, ix, iy + 1, iz + 1), c111 = corner(key, ix + 1, iy + 1, iz + 1);
  // along x first, then y, then z
  const a00 = c000 + (c100 - c000) * sx, a10 = c010 + (c110 - c010) * sx, a01 = c001 + (c101 - c001) * sx, a11 = c011 + (c111 - c011) * sx;
  const b0 = a00 + (a10 - a00) * sy, b1 = a01 + (a11 - a01) * sy;
  const v = b0 + (b1 - b0) * sz;
  const gx = dx * ((c100 - c000) * (1 - sy) * (1 - sz) + (c110 - c010) * sy * (1 - sz) + (c101 - c001) * (1 - sy) * sz + (c111 - c011) * sy * sz);
  const gy = dy * ((a10 - a00) * (1 - sz) + (a11 - a01) * sz);
  const gz = dz * (b1 - b0);
  return { v, gx, gy, gz };
}

const noiseKeys = new Map<string, number>();
function noiseKey(seed: string, planet: Pick<PlanetState, 'id'>): number {
  const id = `${seed}-${planet.id}`;
  let k = noiseKeys.get(id);
  if (k === undefined) { k = hashKey(`${id}-windnoise`); noiseKeys.set(id, k); }
  return k;
}

/**
 * The curl noise at place `n` (unit) at weather time `tau` (segments), as a wind of unit scale along the ground: a scalar
 * noise ψ over the sphere (value noise at n × WIND_FIELD.noise.freq, drifting through time at noise.drift lattice cells a
 * segment), its gradient taken along the ground, turned a quarter about the local up (n × ∇ψ) — so it swirls and has no
 * sources or sinks. Divided by the frequency, so its size is of order 1 whatever the frequency; the field scales it by
 * WIND_FIELD.noise.share of surface gravity. Keyed by seed and planet. Pure.
 */
export function curlNoise(seed: string, planet: Pick<PlanetState, 'id'>, n: Vec3, tau: number): Vec3 {
  const f = WIND_FIELD.noise.freq, t = tau * WIND_FIELD.noise.drift;
  const { gx, gy, gz } = valueNoiseGrad(noiseKey(seed, planet), n.x * f + t, n.y * f + 0.37 * t, n.z * f - 0.61 * t);
  // the gradient along the ground (the part not along the up), turned a quarter about the up
  const along = gx * n.x + gy * n.y + gz * n.z;
  const tx = gx - n.x * along, ty = gy - n.y * along, tz = gz - n.z * along;
  return { x: n.y * tz - n.z * ty, y: n.z * tx - n.x * tz, z: n.x * ty - n.y * tx };
}


/** The cube face point at gnomonic (u, v): face 0 +x, 1 −x, 2 +y, 3 −y, 4 +z, 5 −z — the kernel's face rule (lib.rs wind_grid_at). */
function gridNode(face: number, u: number, v: number): Vec3 {
  switch (face) {
    case 0: return vecNormalize({ x: 1, y: u, z: v });
    case 1: return vecNormalize({ x: -1, y: u, z: v });
    case 2: return vecNormalize({ x: u, y: 1, z: v });
    case 3: return vecNormalize({ x: u, y: -1, z: v });
    case 4: return vecNormalize({ x: u, y: v, z: 1 });
    default: return vecNormalize({ x: u, y: v, z: -1 });
  }
}

/** The second a grid is sampled at: the last WIND_FIELD.gridSeconds boundary (the turn clock reads its round, so any second does). */
export function gridSecond(seconds: number): number {
  return Math.floor(seconds / WIND_FIELD.gridSeconds) * WIND_FIELD.gridSeconds;
}

/**
 * 62.54 step 4: every planet's field sampled for the kernel — `WIND_FIELD.gridN` cells a face edge, 6·(n+1)² nodes a
 * planet, three numbers each, face-major then u then v (the kernel's order), before the gust and the shelter; a planet
 * without a field (dead, missing) all zeros. Null without the wind switch. Pure: the same second gives the same grid.
 */
export function windGrids(state: FieldState, seconds: number): { n: number; data: Float64Array } | null {
  if (!state.config.wind) return null;
  const n = WIND_FIELD.gridN, side = n + 1, per = 6 * side * side * 3;
  const data = new Float64Array(state.planets.length * per);
  for (let pi = 0; pi < state.planets.length; pi++) {
    const p = state.planets[pi];
    if (!p || p.alive === false) continue;
    for (let face = 0; face < 6; face++) for (let i = 0; i <= n; i++) for (let j = 0; j <= n; j++) {
      const w = windField(state, pi, gridNode(face, -1 + (2 * i) / n, -1 + (2 * j) / n), seconds);
      if (!w) continue;
      const k = pi * per + ((face * side + i) * side + j) * 3;
      data[k] = w.x; data[k + 1] = w.y; data[k + 2] = w.z;
    }
  }
  return { n, data };
}

